import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useState } from "react";

export const CommunicationImagePreview = ({
  s3Key,
  onClose,
  onCloseAutoFocus,
}: {
  s3Key: string;
  onClose: () => void;
  onCloseAutoFocus: () => void;
}) => {
  const [zoom, setZoom] = useState(1);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const preview = useQuery({
    ...getItemFromPresignedUrlOptions("message-media", s3Key),
    // Get a fresh URL when opening an older message's attachment.
    refetchOnMount: "always",
  });
  const url = preview.data?.url;
  const hasError = preview.isError || (!!url && failedUrl === url);

  useEffect(() => {
    if (!viewport) return;

    const observer = new ResizeObserver(() => {
      setViewportSize({
        width: viewport.clientWidth,
        height: viewport.clientHeight,
      });
    });
    observer.observe(viewport);
    return () => {
      observer.disconnect();
    };
  }, [viewport]);

  const fitScale =
    imageSize.width && imageSize.height
      ? Math.min(
          1,
          viewportSize.width / imageSize.width,
          viewportSize.height / imageSize.height,
        )
      : 1;
  const imageWidth = imageSize.width * fitScale * zoom;
  const imageHeight = imageSize.height * fitScale * zoom;
  const controlClassName =
    "rounded-full text-white hover:bg-white/15 hover:text-white focus-visible:ring-white";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className="z-[120] bg-black/85 backdrop-blur-md" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[121] text-white outline-none"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onCloseAutoFocus();
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <DialogTitle className="sr-only">Image preview</DialogTitle>
          <DialogDescription className="sr-only">
            Enlarge the image with the zoom controls. Scroll to explore the
            enlarged image. Press Escape to close.
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Close image preview"
              className={`absolute top-4 left-4 bg-black/40 backdrop-blur-md ${controlClassName}`}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogClose>
          <div
            ref={setViewport}
            className="absolute inset-x-6 top-16 bottom-24 overflow-auto overscroll-contain focus-visible:outline focus-visible:outline-white/50"
            tabIndex={0}
            role="region"
            aria-label="Image preview. Scroll to explore when zoomed in."
          >
            {hasError ? (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p role="alert">Unable to load this image.</p>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={preview.isFetching}
                  onClick={() => {
                    setFailedUrl(null);
                    void preview.refetch();
                  }}
                >
                  Try again
                </Button>
              </div>
            ) : url ? (
              <div
                className="flex min-h-full min-w-full items-center justify-center"
                style={{ width: imageWidth, height: imageHeight }}
                onClick={(event) => {
                  if (event.target === event.currentTarget) onClose();
                }}
              >
                <img
                  src={url}
                  alt="Text message attachment"
                  className="shrink-0 object-contain shadow-2xl"
                  style={
                    imageSize.width
                      ? { width: imageWidth, height: imageHeight }
                      : { maxWidth: "100%", maxHeight: "100%" }
                  }
                  crossOrigin="anonymous"
                  onLoad={(event) => {
                    setImageSize({
                      width: event.currentTarget.naturalWidth,
                      height: event.currentTarget.naturalHeight,
                    });
                  }}
                  onError={() => {
                    setFailedUrl(url);
                  }}
                />
              </div>
            ) : (
              <div
                className="flex h-full items-center justify-center"
                role="status"
              >
                <LoadingSpinner />
                <span className="sr-only">Loading image</span>
              </div>
            )}
          </div>
          <div
            className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-neutral-900/80 p-1.5 shadow-xl backdrop-blur-md"
            role="group"
            aria-label="Image zoom controls"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={controlClassName}
              aria-label="Zoom out"
              disabled={zoom === 1 || hasError || !url}
              onClick={() => {
                setZoom((value) => Math.max(1, value - 0.5));
              }}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span
              className="w-12 text-center text-xs tabular-nums"
              aria-live="polite"
            >
              {Math.round(zoom * 100)}%
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={controlClassName}
              aria-label="Zoom in"
              disabled={zoom === 4 || hasError || !url}
              onClick={() => {
                setZoom((value) => Math.min(4, value + 0.5));
              }}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
